import React, { useState, useEffect, useCallback, useRef } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, doc, getDoc, updateDoc, query, orderBy } from 'firebase/firestore';
import { FaFilePdf, FaSave, FaArrowRight, FaWallet, FaCalculator } from 'react-icons/fa';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useNavigate } from 'react-router-dom';

const BookingReport = () => {
  const navigate = useNavigate();
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear().toString();
  const currentMonth = new Date().toISOString().slice(5, 7);
  const [filterYear, setFilterYear] = useState(currentYear);
  const [filterMonth, setFilterMonth] = useState(currentMonth);
  const [companies, setCompanies] = useState([]);
  const [bookingAgents, setBookingAgents] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [awbSearch, setAwbSearch] = useState('');
  const [bookingInputs, setBookingInputs] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const tableRef = useRef(null);

  const months = [
    { value: '01', label: 'January' }, { value: '02', label: 'February' }, { value: '03', label: 'March' },
    { value: '04', label: 'April' }, { value: '05', label: 'May' }, { value: '06', label: 'June' },
    { value: '07', label: 'July' }, { value: '08', label: 'August' }, { value: '09', label: 'September' },
    { value: '10', label: 'October' }, { value: '11', label: 'November' }, { value: '12', label: 'December' }
  ];

  useEffect(() => {
    fetchCompanies();
    fetchBookingAgents();
  }, []);

  const fetchCompanies = async () => {
    try {
        const q = query(collection(db, 'companies'), orderBy('name'));
        const snap = await getDocs(q);
        setCompanies(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) { console.error("Error fetching companies:", error); }
  };

  const fetchBookingAgents = async () => {
    try {
        const q = query(collection(db, 'booking_companies'), orderBy('name'));
        const snap = await getDocs(q);
        setBookingAgents(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) { console.error("Error fetching booking agents:", error); }
  };

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'invoices'), orderBy('invoiceDate', 'desc'));
      const querySnapshot = await getDocs(q);
      
      let flattenedItems = [];
      querySnapshot.docs.forEach(docSnap => {
        const invoice = docSnap.data();
        const invoiceId = docSnap.id;
        let invoiceMonth = invoice.invoiceYearMonth;
        
        if(!invoiceMonth && invoice.invoiceDate) {
            invoiceMonth = invoice.invoiceDate.slice(0, 7);
        }
        
        const matchesYear = invoiceMonth && invoiceMonth.startsWith(filterYear);
        const matchesMonth = filterMonth === 'All' ? true : invoiceMonth === `${filterYear}-${filterMonth}`;
        
        if (matchesYear && matchesMonth && invoice.items && Array.isArray(invoice.items)) {
            invoice.items.forEach(item => {
                const rate = parseFloat(item.rate || 0);
                const fsc = parseFloat(item.fsc || 0);
                const demand = parseFloat(item.demandCharge || 0);
                const exchangeRate = parseFloat(invoice.exchangeRate || 1);
                
                // --- BDT LOGIC ---
                let totalUsd = 0;
                if (item.rateCurrency === 'BDT') {
                    const weight = parseFloat(item.weight || 0);
                    totalUsd = (rate * weight) / exchangeRate;
                } else {
                    totalUsd = rate + fsc + demand;
                }
                const partyTotalBdt = totalUsd * exchangeRate;
                
                flattenedItems.push({
                    uniqueKey: `${invoiceId}_${item.id}`,
                    invoiceId: invoiceId,
                    itemId: item.id,
                    date: item.date,
                    country: item.country,
                    partyName: invoice.companyName || invoice.customerName || 'Unknown',
                    awbNo: item.awbNo || '',
                    shipment: item.shipment,
                    service: item.service,
                    weight: item.weight,
                    bookingWeight: item.bookingWeight || item.weight,
                    partyTotal: partyTotalBdt,
                    bookingTotal: item.bookingTotal || 0,
                    bookingAgentId: item.bookingAgentId || '', 
                    profit: item.profit || 0,
                    month: invoiceMonth
                });
            });
        }
      });

      flattenedItems.sort((a, b) => {
          const parseDate = (dStr) => {
              if (!dStr) return 0;
              if (dStr.match(/^\d{2}[-/]\d{2}[-/]\d{4}$/)) {
                  const p = dStr.split(/[-/]/);
                  return new Date(`${p[2]}-${p[1]}-${p[0]}`).getTime();
              }
              return new Date(dStr).getTime();
          };
          return parseDate(a.date) - parseDate(b.date);
      });

      setReportData(flattenedItems);
    } catch (error) { console.error("Error fetching report data:", error); }
    setLoading(false);
  }, [filterYear, filterMonth]);

  useEffect(() => {
    fetchReportData();
    setBookingInputs({}); 
    setHasChanges(false);
  }, [fetchReportData, filterYear, filterMonth]);

  const handleInputChange = (uniqueKey, field, value) => {
    setBookingInputs(prev => ({
        ...prev,
        [uniqueKey]: {
            ...prev[uniqueKey],
            [field]: value
        }
    }));
    setHasChanges(true); 
  };

  const handleAgentChangeWithConfirm = (uniqueKey, currentAgentId, newAgentId) => {
    if (currentAgentId && currentAgentId !== newAgentId) {
        const confirmChange = window.confirm("Are you sure you want to change the previously selected agent?");
        if (!confirmChange) return;
    }
    handleInputChange(uniqueKey, 'bookingAgentId', newAgentId);
  };

  const filteredData = reportData.filter(item => {
      if (selectedCompany && item.partyName !== selectedCompany) return false;
      if (awbSearch) {
          const searchStr = awbSearch.toLowerCase().trim();
          const itemAwb = (item.awbNo || '').toString().toLowerCase();
          if (!itemAwb.includes(searchStr)) return false;
      }
      return true;
  });

  const handleApplyBelow = (startIndex, agentId) => {
    if (filterMonth === 'All') {
        alert('"Apply Below" works only when a specific month is selected. It does not work in "All Month" view.');
        return;
    }
    
    const confirmChange = window.confirm("Apply this agent to empty rows below?");
    if (!confirmChange) return;

    const newInputs = { ...bookingInputs };
    for (let i = startIndex + 1; i < filteredData.length; i++) {
        const row = filteredData[i];
        const rowInput = newInputs[row.uniqueKey] || {};
        const currentRowAgentId = rowInput.bookingAgentId !== undefined ? rowInput.bookingAgentId : row.bookingAgentId;
        
        if (currentRowAgentId) {
            break; 
        }
        newInputs[row.uniqueKey] = {
            ...rowInput,
            bookingAgentId: agentId
        };
    }
    setBookingInputs(newInputs);
    setHasChanges(true);
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
        const changedKeys = Object.keys(bookingInputs);
        if (changedKeys.length === 0) { setSaving(false); return; }
        
        const updatesByInvoice = {};
        
        changedKeys.forEach(uniqueKey => {
            const row = reportData.find(r => r.uniqueKey === uniqueKey);
            if (row) {
                if (!updatesByInvoice[row.invoiceId]) updatesByInvoice[row.invoiceId] = [];
                
                const inputData = bookingInputs[uniqueKey] || {};
                
                const newBookingTotal = inputData.bookingTotal !== undefined ? parseFloat(inputData.bookingTotal) : row.bookingTotal;
                const newBookingWeight = inputData.bookingWeight !== undefined ? inputData.bookingWeight : row.bookingWeight;
                const newAgentId = inputData.bookingAgentId !== undefined ? inputData.bookingAgentId : row.bookingAgentId;
                
                const agentObj = bookingAgents.find(a => a.id === newAgentId);
                const newAgentName = agentObj ? agentObj.name : '';

                if (!isNaN(newBookingTotal)) {
                    updatesByInvoice[row.invoiceId].push({ 
                        itemId: row.itemId, 
                        bookingTotal: newBookingTotal, 
                        partyTotal: row.partyTotal,
                        bookingAgentId: newAgentId,
                        bookingAgentName: newAgentName,
                        bookingWeight: newBookingWeight
                    });
                }
            }
        });

        const promises = Object.keys(updatesByInvoice).map(async (invoiceId) => {
            const invoiceRef = doc(db, 'invoices', invoiceId);
            const invoiceSnap = await getDoc(invoiceRef);
            if (invoiceSnap.exists()) {
                const invoiceData = invoiceSnap.data();
                const updatesForItem = updatesByInvoice[invoiceId];
                
                const updatedItems = invoiceData.items.map(item => {
                    const updateData = updatesForItem.find(u => u.itemId === item.id);
                    if (updateData) {
                        const newProfit = updateData.bookingTotal > 0 ? (updateData.partyTotal - updateData.bookingTotal) : 0;
                        return { 
                            ...item, 
                            bookingTotal: updateData.bookingTotal, 
                            profit: newProfit,
                            bookingAgentId: updateData.bookingAgentId,
                            bookingAgentName: updateData.bookingAgentName,
                            bookingWeight: updateData.bookingWeight
                        };
                    }
                    return item;
                });
                return updateDoc(invoiceRef, { items: updatedItems });
            }
        });
        
        await Promise.all(promises);
        alert("All changes saved successfully!");
        setBookingInputs({});
        setHasChanges(false);
        fetchReportData(); 
    } catch (error) { console.error("Error saving bulk data:", error); alert("Error saving data."); }
    setSaving(false);
  };

  const awbCounts = {};
  filteredData.forEach(item => {
      const awb = (item.awbNo || '').toString().trim().toLowerCase();
      if (awb && awb !== '-' && awb !== 'n/a') { awbCounts[awb] = (awbCounts[awb] || 0) + 1; }
  });

  const totals = filteredData.reduce((acc, row) => {
      const inputData = bookingInputs[row.uniqueKey] || {};
      const displayBookingTotal = inputData.bookingTotal !== undefined ? parseFloat(inputData.bookingTotal) : (row.bookingTotal || 0);
      const displayProfit = displayBookingTotal > 0 ? (row.partyTotal - displayBookingTotal) : 0;
      
      acc.partyTotal += row.partyTotal || 0;
      acc.bookingTotal += displayBookingTotal;
      acc.profit += displayProfit;
      return acc;
  }, { partyTotal: 0, bookingTotal: 0, profit: 0 });

  const generatePDF = (action = 'download') => {
    setIsProcessing(true);
    try {
        const doc = new jsPDF('l', 'mm', 'a4'); 
        doc.setFontSize(16); doc.setTextColor(44, 62, 80); doc.text("PROFIT/LOSS REPORT", 14, 15);
        doc.setFontSize(10); doc.setTextColor(100);
        const periodText = filterMonth !== 'All' ? `${months.find(m=>m.value===filterMonth)?.label} ${filterYear}` : `ALL MONTHS ${filterYear}`;
        doc.text(`Period: ${periodText} | Party: ${selectedCompany || 'All Parties'}`, 14, 22);

        const tableBody = filteredData.map(row => {
            const inputData = bookingInputs[row.uniqueKey] || {};
            const displayBookingTotal = inputData.bookingTotal !== undefined ? parseFloat(inputData.bookingTotal) : (row.bookingTotal || 0);
            const displayBookingWeight = inputData.bookingWeight !== undefined ? inputData.bookingWeight : (row.bookingWeight || 0);
            const displayProfit = displayBookingTotal > 0 ? (row.partyTotal - displayBookingTotal) : 0;
            return [
                row.date, row.country, row.partyName, row.awbNo, row.shipment, row.service, `${Number(row.weight || 0).toFixed(1)} KG`, `${Number(displayBookingWeight).toFixed(1)} KG`,
                row.partyTotal.toFixed(2), displayBookingTotal.toFixed(2), displayProfit !== 0 ? displayProfit.toFixed(2) : '-'
            ];
        });

        const tableFoot = [[ 'TOTAL SUMMARY', '', '', '', '', '', '', '', totals.partyTotal.toFixed(2), totals.bookingTotal.toFixed(2), totals.profit.toFixed(2) ]];

        autoTable(doc, {
            head: [['Date', 'Country', 'Party', 'AWB No.', 'Shipment', 'Service', 'Party Wt', 'Booking Wt', 'Party Total', 'Booking Cost', 'Profit']],
            body: tableBody, foot: tableFoot, startY: 28, theme: 'grid',
            styles: { fontSize: 8, cellPadding: 2, valign: 'middle', textColor: 20 },
            headStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold' },
            footStyles: { fillColor: [236, 240, 241], textColor: [44, 62, 80], fontStyle: 'bold', halign: 'right' },
            columnStyles: { 8: { halign: 'right' }, 9: { halign: 'right' }, 10: { halign: 'right', fontStyle: 'bold' } }
        });

        const fileName = `Report_${filterMonth !== 'All' ? filterMonth : 'ALL'}_${filterYear}.pdf`;
        if (action === 'download') doc.save(fileName);
        else if (action === 'share') navigator.share({ files: [new File([doc.output('blob')], fileName, { type: "application/pdf" })] }).catch(err=>console.log(err));
    } catch (error) { console.error(error); alert("PDF Error"); } finally { setIsProcessing(false); }
  };

  const containerStyle = { padding: '20px', background: '#f4f6f8', minHeight: '100vh', fontFamily: "'Segoe UI', sans-serif" };
  const tableStyle = { width: '100%', borderCollapse: 'collapse', background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: '8px', overflow: 'hidden' };
  const thStyle = { background: '#2c3e50', color: 'white', padding: '12px 5px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' };
  const tdStyle = { padding: '8px 5px', borderBottom: '1px solid #eee', color: '#333', fontSize: '12px', verticalAlign: 'middle' };
  const inputStyle = { padding: '6px', border: '1px solid #3498db', borderRadius: '4px', width: '70px', textAlign: 'right', fontWeight: 'bold', outline: 'none', background: '#f0f8ff' };
  const selectStyle = { padding: '6px', border: '1px solid #ccc', borderRadius: '4px', width: '100%', fontSize: '11px' };

  return (
    <div style={containerStyle}>
      <style>{`input[type=number]::-webkit-inner-spin-button, input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; } input[type=number] { -moz-appearance: textfield; }`}</style>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '25px' }}>
          <div onClick={() => navigate('/dashboard/booking-accounts')} style={{ background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)', padding: '15px 20px', borderRadius: '10px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white', boxShadow: '0 4px 10px rgba(0,0,0,0.1)', transition: 'transform 0.2s' }}>
            <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                <div style={{background: 'rgba(255,255,255,0.2)', padding: '10px', borderRadius: '50%'}}><FaWallet size={20} color="white" /></div>
                <div><h3 style={{margin: 0, fontSize: '16px'}}>Payable Accounts</h3><p style={{margin: '2px 0 0', fontSize: '12px', opacity: 0.9}}>Manage Agent Payments</p></div>
            </div>
            <div style={{background: 'white', color: '#3498db', padding: '6px 15px', borderRadius: '20px', fontWeight: 'bold', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px'}}>Enter <FaArrowRight /></div>
          </div>
          
          <div onClick={() => navigate('/dashboard/booking-rates')} style={{ background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)', padding: '15px 20px', borderRadius: '10px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white', boxShadow: '0 4px 10px rgba(0,0,0,0.1)', transition: 'transform 0.2s' }}>
            <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                <div style={{background: 'rgba(255,255,255,0.2)', padding: '10px', borderRadius: '50%'}}><FaCalculator size={20} color="white" /></div>
                <div><h3 style={{margin: 0, fontSize: '16px'}}>Booking Rates</h3><p style={{margin: '2px 0 0', fontSize: '12px', opacity: 0.9}}>Set Agent Prices (DHL/FedEx)</p></div>
            </div>
            <div style={{background: 'white', color: '#27ae60', padding: '6px 15px', borderRadius: '20px', fontWeight: 'bold', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px'}}>Enter <FaArrowRight /></div>
          </div>
      </div>

      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
        <h2 style={{color: '#2c3e50', margin: 0}}>Booking Profit Report</h2>
        <div style={{display: 'flex', gap: '10px'}}>
             <button onClick={() => generatePDF('download')} disabled={isProcessing} style={{background: '#e74c3c', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px'}}><FaFilePdf /> PDF</button>
        </div>
      </div>

      <div className="no-print" style={{background: 'white', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', marginBottom: '20px'}}>
          <div style={{display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'center'}}>
              <div style={{display: 'flex', gap: '5px', alignItems: 'center'}}>
                  <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} style={{padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}>
                      <option value="All">All Month</option>
                      {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  <input type="number" value={filterYear} onChange={(e) => setFilterYear(e.target.value)} style={{padding: '8px', border: '1px solid #ddd', borderRadius: '4px', width: '80px', textAlign: 'center'}}/>
              </div>
              
              <select value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} style={{padding: '8px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '150px'}}><option value="">All Clients</option>{companies.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}</select>
              <input type="text" placeholder="Search AWB..." value={awbSearch} onChange={(e) => setAwbSearch(e.target.value)} style={{padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}/>
          </div>
      </div>

      {loading ? <p>Loading...</p> : (
        <div ref={tableRef} style={{overflowX: 'auto', paddingBottom: '20px', background: 'white', padding: '10px', borderRadius: '8px'}}>
            <table style={tableStyle}>
                <thead>
                    <tr>
                        <th style={thStyle}>Date</th>
                        <th style={thStyle}>Client</th>
                        <th style={thStyle}>AWB No.</th>
                        <th style={{...thStyle, width: '80px'}}>Party Weight</th>
                        <th style={{...thStyle, width: '90px'}}>Booking Weight</th>
                        <th style={{...thStyle, textAlign: 'right'}}>Party Total</th>
                        <th style={{...thStyle, width: '180px'}}>Booking By (Agent)</th>
                        <th style={{...thStyle, textAlign: 'right'}}>Booking Cost</th>
                        <th style={{...thStyle, textAlign: 'right'}}>Profit</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredData.length > 0 ? filteredData.map((row, index) => {
                        const inputData = bookingInputs[row.uniqueKey] || {};
                        const displayBookingTotal = inputData.bookingTotal !== undefined ? parseFloat(inputData.bookingTotal) : (row.bookingTotal || 0);
                        const displayAgentId = inputData.bookingAgentId !== undefined ? inputData.bookingAgentId : row.bookingAgentId;
                        const displayBookingWeight = inputData.bookingWeight !== undefined ? inputData.bookingWeight : (row.bookingWeight || '');
                        const displayProfit = displayBookingTotal > 0 ? (row.partyTotal - displayBookingTotal) : 0;
                        const awbKey = (row.awbNo || '').toString().trim().toLowerCase();
                        
                        const nextRow = filteredData[index + 1];
                        let nextRowAgentId = '';
                        if (nextRow) {
                            const nextInput = bookingInputs[nextRow.uniqueKey] || {};
                            nextRowAgentId = nextInput.bookingAgentId !== undefined ? nextInput.bookingAgentId : (nextRow.bookingAgentId || '');
                        }
                        const showApplyButton = displayAgentId && displayAgentId !== '' && filterMonth !== 'All' && nextRow && nextRowAgentId === '';
                        
                        return (
                            <tr key={row.uniqueKey} style={{background: awbCounts[awbKey] > 1 ? '#ffe6e6' : 'white', borderBottom: '1px solid #f1f1f1'}}>
                                <td style={tdStyle}>{row.date}</td>
                                <td style={tdStyle}>{row.partyName}</td>
                                <td style={tdStyle}>{row.awbNo}</td>
                                
                                <td style={{...tdStyle, fontWeight: 'bold'}}>
                                    {Number(row.weight || 0).toFixed(1)} KG
                                </td>
                                
                                <td style={tdStyle}>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '3px'}}>
                                        <input 
                                            type="number" 
                                            step="0.1"
                                            value={displayBookingWeight} 
                                            onChange={(e) => handleInputChange(row.uniqueKey, 'bookingWeight', e.target.value)} 
                                            style={{...inputStyle, width: '60px', padding: '5px'}} 
                                            onWheel={(e) => e.target.blur()} 
                                        /> <span style={{fontSize: '11px'}}>KG</span>
                                    </div>
                                </td>

                                <td style={{...tdStyle, textAlign: 'right', fontWeight: 'bold', color: '#2980b9'}}>{row.partyTotal.toFixed(2)}</td>
                                
                                <td style={tdStyle}>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                                        <select 
                                            value={displayAgentId || ''} 
                                            onChange={(e) => handleAgentChangeWithConfirm(row.uniqueKey, displayAgentId, e.target.value)} 
                                            style={{...selectStyle, flex: 1}}
                                        >
                                            <option value="">Select Agent</option>
                                            {bookingAgents.map(agent => (
                                                <option key={agent.id} value={agent.id}>{agent.name}</option>
                                            ))}
                                        </select>
                                        
                                        {showApplyButton && (
                                            <button 
                                                onClick={() => handleApplyBelow(index, displayAgentId)} 
                                                title="Apply to empty rows below"
                                                style={{
                                                    background: '#3498db', color: 'white', border: 'none', 
                                                    borderRadius: '4px', padding: '6px', fontSize: '12px', 
                                                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    minWidth: '28px'
                                                }}
                                            >
                                                <FaArrowRight />
                                            </button>
                                        )}
                                    </div>
                                </td>

                                <td style={{...tdStyle, textAlign: 'right'}}>
                                    <input 
                                        type="number" 
                                        value={inputData.bookingTotal !== undefined ? inputData.bookingTotal : (row.bookingTotal > 0 ? row.bookingTotal : '')} 
                                        placeholder="0"
                                        onChange={(e) => handleInputChange(row.uniqueKey, 'bookingTotal', e.target.value)} 
                                        style={inputStyle} 
                                        onWheel={(e) => e.target.blur()} 
                                    />
                                </td>
                                
                                <td style={{...tdStyle, textAlign: 'right', fontWeight: 'bold', color: displayProfit < 0 ? '#e74c3c' : '#27ae60'}}>
                                    {displayProfit !== 0 ? displayProfit.toFixed(2) : '-'}
                                </td>
                            </tr>
                        );
                    }) : <tr><td colSpan="9" style={{padding: '20px', textAlign: 'center'}}>No data found.</td></tr>}
                </tbody>
                {filteredData.length > 0 && (
                    <tfoot>
                        <tr style={{background: '#ecf0f1', fontWeight: 'bold'}}>
                            <td colSpan="5" style={{padding: '10px', textAlign: 'right'}}>TOTAL:</td>
                            <td style={{padding: '10px', textAlign: 'right', color: '#2980b9'}}>{totals.partyTotal.toFixed(2)}</td>
                            <td></td>
                            <td style={{padding: '10px', textAlign: 'right'}}>{totals.bookingTotal.toFixed(2)}</td>
                            <td style={{padding: '10px', textAlign: 'right', color: totals.profit < 0 ? '#e74c3c' : '#27ae60'}}>{totals.profit.toFixed(2)}</td>
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
      )}
      
      <div style={{ height: '80px' }}></div>

      <button onClick={handleSaveAll} disabled={!hasChanges || saving} style={{position: 'fixed', bottom: '30px', right: '30px', background: hasChanges ? '#27ae60' : '#95a5a6', color: 'white', padding: '12px 25px', borderRadius: '30px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.2)', fontSize: '14px', fontWeight: 'bold', cursor: hasChanges ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: '8px'}}>
        <FaSave /> {saving ? 'Saving...' : 'Save Changes'}
      </button>
    </div>
  );
};

export default BookingReport;